import { memo, useCallback, useEffect, useState } from "react";
import {
  DesktopSATechValuesFilter,
  MobileSATechValuesFilter,
} from "~/features/selectionAssistant/bearing/components/Filters/TechFilters/SATechValuesFilter";
import { CharacteristicsFilter } from "~/features/selectionAssistant/bearing/components/Filters/CharacteristicsFilter";
import { SATaxonomyFilter } from "~/features/selectionAssistant/bearing/components/Filters/SATaxonomyFilter";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { AccordionGroup, AccordionGroupTitle } from "~/common/mui/Accordion/AccordionGroup";
import { Card } from "~/features/configurator/Linear/components/Card";
import { changeSATab } from "~/features/selectionAssistant/bearing/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { applyAllFiltersAction } from "~/features/search/redux/actions";
import { selectAppliedTechFilterValues } from "~/features/search/redux/searchSelectors";
import {
  flattenTechFilterFormData,
  unflattenTechFilterFormData,
} from "~/features/search/utils/flattenTechFilterFormData";
import { TechFilterResetContext } from "~/features/search/components/Filters/TechFilterResetContext";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  styled,
  Tab,
  TabContext,
  TabList,
  TabPanel,
} from "~/common/mui";

export const SAFilters = memo(() => {
  const isDesktop = useIsMinDesktopSize();
  const appliedTechFilterValues = useSelector(selectAppliedTechFilterValues);
  const dispatch = useDispatch();

  const methods = useForm({
    values: unflattenTechFilterFormData(appliedTechFilterValues),
    mode: "onChange",
  });

  useEffect(() => {
    methods.reset();
  }, [appliedTechFilterValues, methods]);

  const onSubmit = (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => {
    const flattenedData = flattenTechFilterFormData(data);
    dispatch(applyAllFiltersAction(flattenedData));
  };

  const resetFilters = useCallback(
    (filterUids?: string[]) => {
      const currentValues = methods.getValues();

      // If specific UIDs provided, clear only those. Otherwise, get all tech filter UIDs from current values
      const uidsToReset = filterUids || Object.keys(currentValues);

      uidsToReset.forEach((uid) => {
        methods.setValue(`${uid}.min`, undefined, { shouldDirty: true, shouldTouch: true });
        methods.setValue(`${uid}.max`, undefined, { shouldDirty: true, shouldTouch: true });
      });

      // Submit the form to trigger the search
      methods.handleSubmit(onSubmit)();
    },
    [methods, onSubmit],
  );

  return (
    <TechFilterResetContext.Provider value={{ resetFilters }}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>{isDesktop ? <DesktopSAFilters /> : <MobileSAFilters />}</form>
      </FormProvider>
    </TechFilterResetContext.Provider>
  );
});

const MOBILE_FILTER_CONFIG = [
  {
    title: "page.selectionAssistant.characteristic&type",
    elements: [
      {
        title: "page.selectionAssistant.tab.characteristics",
        content: <CharacteristicsFilter />,
      },
      {
        title: "page.selectionAssistant.tab.bearing",
        content: <SATaxonomyFilter />,
      },
    ],
  },
  {
    title: "page.selectionAssistant.dimensions&loadrating",
    elements: [
      {
        title: "page.selectionAssistant.tab.dimensions",
        content: <MobileSATechValuesFilter index={0} />,
      },
      {
        title: "page.selectionAssistant.tab.loadRating",
        content: <MobileSATechValuesFilter index={1} />,
      },
    ],
  },
];

const CsticTabGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  columnGap: "var(--gutter)",
  rowGap: theme.spacing(4),
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: `${theme.spacing(4)} 0`,
}));

const MobileSAFilters = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Stack direction="column" spacing={3}>
      <div className={"fullbleed"}>
        {MOBILE_FILTER_CONFIG.map((group, groupIndex) => (
          <AccordionGroup key={groupIndex}>
            <AccordionGroupTitle>{t(group.title)}</AccordionGroupTitle>
            {group.elements.map((element, elementIndex) => (
              <Accordion key={elementIndex}>
                <AccordionSummary>{t(element.title)}</AccordionSummary>
                <AccordionDetails>{element.content}</AccordionDetails>
              </Accordion>
            ))}
          </AccordionGroup>
        ))}
      </div>
    </Stack>
  );
};

const DesktopSAFilters = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("Characteristics");
  const dispatch = useDispatch();

  const changeTab = (val: string) => {
    setActiveTab(val);
    dispatch(changeSATab(val));
  };

  return (
    <TabContext value={activeTab}>
      <TabList onChange={(e, val) => changeTab(val)} variant={"standard"}>
        <Tab
          data-test-id={"selectionassistant-characteristic-type"}
          value={"Characteristics"}
          label={t("page.selectionAssistant.characteristic&type")}
        />
        <Tab
          data-test-id={"selectionassistant-dimensions-loadrating"}
          value={"Dimensions"}
          label={t("page.selectionAssistant.dimensions&loadrating")}
        />
      </TabList>

      <TabPanel value={"Characteristics"} keepMounted>
        <CsticTabGrid>
          <Box sx={{ gridRow: "1 / 2" }}>
            <Title>{t("page.selectionAssistant.characteristicsProperties")}</Title>
            <Description>{t("page.selectionAssistant.info.characteristicsProperties")}</Description>
          </Box>

          <Box sx={{ gridRow: "2 / 3", display: "flex" }}>
            <Card disableHover={true} style={{ width: "100%" }}>
              <Box sx={{ p: { phone: 2, "desktop-m": 4 } }}>
                <CharacteristicsFilter />
              </Box>
            </Card>
          </Box>

          <Box sx={{ gridRow: "1 / 2" }}>
            <Title>{t("page.selectionAssistant.bearingType")}</Title>
            <Description>{t("page.selectionAssistant.info.bearingType")}</Description>
          </Box>

          <Box sx={{ gridRow: "2 / 3", display: "flex" }}>
            <Card disableHover={true} style={{ width: "100%" }}>
              <Box sx={{ p: { phone: 2, "desktop-m": 4 } }}>
                <SATaxonomyFilter />
              </Box>
            </Card>
          </Box>
        </CsticTabGrid>

        {/*<ResetAllBtn onClick={() => dispatch(resetSAFilters())} />*/}
        {/*</div>*/}
      </TabPanel>

      <TabPanel value={"Dimensions"} keepMounted>
        <DesktopSATechValuesFilter />
      </TabPanel>
    </TabContext>
  );
};

const Title = styled("h3")(({ theme }) => ({
  ...theme.typography.headlineSmall,
  marginTop: 0,
  marginBottom: 0,
}));

const Description = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2.5),
}));
